import { createModel, RematchDispatch } from '@rematch/core';
import { produce } from 'immer';

import { RootModel } from '../models';
import { storeName as layoutModelStore } from '../models/layout.model';
import { ProjectChildItemTypes, SitePlanFilterTypes } from '../../types';
import { convertProjectChildItemResponse } from '../../utils';
import {
  filterChildItems,
  getChildItemByKey,
  getReviewsByCode,
  getSimilarChildItemByKey,
} from '../../services/projectChildItem.service';
import { ProjectChildItemResponseTypes } from '../../types/response/projectChildItem';
import { storeName as homeProfileModel } from '../models/homeProfile.model';

import baseEntity from './base.entity';

export const storeName = 'projectChildItemEntity';

export const projectChildItemEntity = createModel<RootModel>()({
  name: storeName,
  state: { ids: {} },
  reducers: {
    ...baseEntity,
    saves: produce((state, entities: ProjectChildItemTypes[]) => {
      (entities || []).forEach((entity: ProjectChildItemTypes) => {
        state.ids[entity.id] = entity;
      });
    }),
  },
  effects: (dispatch: RematchDispatch<typeof projectChildItemEntity>) => ({
    async filter(payload: SitePlanFilterTypes & { keyword?: string }, state: RootModel) {
      const { locale } = state[layoutModelStore];
      const resp: ProjectChildItemResponseTypes[] = await filterChildItems(
        payload,
        locale.apiLanguage
      );
      const newItems: ProjectChildItemTypes[] = resp.map((i) =>
        convertProjectChildItemResponse(i, locale.locale)
      );
      this.saves(newItems);
      return newItems;
    },
    async loadByKey(payload: string, state: RootModel) {
      dispatch[homeProfileModel].setLoading(true);
      const result = await fetchByKey(payload, state);
      dispatch[homeProfileModel].setLoading(false);
      if (result.notFound) return { notFound: true };

      this.saves([result.item]);

      return result.item;
    },
    async loadSimilarByKey(payload, state: RootModel) {
      const { locale } = state[layoutModelStore];
      const { id, key, page = 0 } = payload;
      const projectChildItem = { ...state[storeName].ids[id] };

      if (!projectChildItem) return;

      const resp = await getSimilarChildItemByKey(key, locale.apiLanguage, page);
      const newItems = [];
      resp.content.forEach((item) => {
        const entities = state[storeName].ids;
        const findItem = entities[item.id];
        if (!findItem) {
          newItems.push(item);
        }
      });

      const items = newItems.map((item) => convertProjectChildItemResponse(item, locale.locale));
      this.saveSimplified(items);
      const { similarItem } = projectChildItem;
      let dataSimilarItemIds = similarItem ? similarItem.ids : [];
      // TODO update code
      const itemsId: number[] = resp.content.map((item) => item.id);
      dataSimilarItemIds = dataSimilarItemIds.concat(itemsId);
      const newSimilarItem = {
        ids: dataSimilarItemIds,
        pageNumber: resp.number,
        isLast: resp.last,
      };
      projectChildItem.similarItem = newSimilarItem;
      dispatch[storeName].saves([projectChildItem]);
      return resp;
    },
    async loadSelfByKey(payload: string, state: RootModel) {
      dispatch[homeProfileModel].setLoading(true);
      const result = await fetchByKey(payload, state);
      dispatch[homeProfileModel].setLoading(false);
      if (result.notFound) return { notFound: true };

      this.saves([result.item]);

      return { item: result.item, simplifiedItem: result.simplifiedItem };
    },
  }),
});

export default projectChildItemEntity;

const fetchByKey = async (payload: string, state: RootModel): Promise<any> => {
  const { locale } = state[layoutModelStore];

  const entity: { [key: string]: ProjectChildItemTypes } = state[storeName].ids;
  const projectChildItem = Object.values(entity).find(
    (item: ProjectChildItemTypes) => (item.alias || item.code) === payload
  );
  if (projectChildItem && !projectChildItem.isSimplified) return { item: projectChildItem };

  const resp: ProjectChildItemResponseTypes & { notFound?: boolean } = await getChildItemByKey(
    payload,
    locale.apiLanguage
  );
  if (resp.notFound) return { notFound: true };

  const item = convertProjectChildItemResponse(resp, locale.locale);
  // get data rating review homeprofile
  if (resp.code) {
    const respReview = await getReviewsByCode(resp.code, locale.apiLanguage);
    item.reviews = respReview;
  }

  if (projectChildItem) {
    item.isShowSquareConfig = projectChildItem.isShowSquareConfig;
    item.isShowDirectionConfig = projectChildItem.isShowDirectionConfig;
  }

  return { item, simplifiedItem: resp };
};
